import { SHORT_TERM_MAX_CONTINUOUS_DAYS } from "../config/careShiftWindows";

/** Booking days at or below this use per-day rates; above uses monthly salary. */
export const MONTHLY_SALARY_MIN_BOOKING_DAYS = SHORT_TERM_MAX_CONTINUOUS_DAYS + 1;

/**
 * Map CareHub search shift → Pricing & Packages shiftPricing key.
 * @returns {"day12"|"night12"|"live24"|null}
 */
export function resolveShiftPricingKey(searchShift) {
  const raw = String(searchShift || "")
    .trim()
    .toLowerCase();
  if (!raw || raw === "hourly" || raw === "hour" || raw === "4" || raw === "4h") return null;
  if (raw === "12_night" || raw === "12night" || (raw.includes("12") && raw.includes("night"))) {
    return "night12";
  }
  if (raw === "12_day" || raw === "12day" || (raw.includes("12") && raw.includes("day"))) {
    return "day12";
  }
  if (
    raw === "24h" ||
    raw === "24" ||
    raw === "24hour" ||
    raw === "24hours" ||
    raw.includes("live") ||
    (raw.includes("24") && !raw.includes("12"))
  ) {
    return "live24";
  }
  return null;
}

function readPositiveInr(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.round(n) : null;
}

/**
 * Platform default per-day rates shown when a caregiver has enabled a shift
 * (via Availability) but hasn't entered a price for it on Pricing & Plans yet.
 * Mirrors the pre-fill defaults in CaregiverPricingPackages.js so the price the
 * caregiver sees while filling in their pricing matches what the customer sees.
 */
export const SHIFT_DEFAULT_PRICE_INR = {
  day12: 950,
  night12: 1000,
  live24: 1500,
};

/** shiftPricing object from caregiver row / profileExtended. */
export function getCaregiverShiftPricing(caregiver) {
  if (!caregiver || typeof caregiver !== "object") return null;
  const direct = caregiver.shiftPricing;
  if (direct && typeof direct === "object") return direct;
  const extended =
    caregiver.profileExtended?.shiftPricing ||
    caregiver.profile_extended?.shiftPricing ||
    null;
  return extended && typeof extended === "object" ? extended : null;
}

/**
 * Per-day rate from caregiver Pricing & Packages (12h day / night / 24h).
 * Never falls back to estimatedCaregiverFeeInr (that can still carry legacy
 * catalog invents like ₹5500). If the caregiver has enabled the shift but
 * hasn't entered a price yet, use the platform default rate instead — an
 * explicit `active: false` opt-out still returns null, no default shown.
 */
export function getShiftDailyRateInr(caregiver, searchShift) {
  const key = resolveShiftPricingKey(searchShift);
  if (!key) return null;
  const pricing = getCaregiverShiftPricing(caregiver);
  const row = pricing?.[key];
  if (row && row.active === false) return null;
  if (row) {
    const fromShift = readPositiveInr(row.priceInr ?? row.price);
    if (fromShift != null) return fromShift;
  }
  return SHIFT_DEFAULT_PRICE_INR[key] ?? null;
}

export function getShiftMonthlyRateInr(caregiver, searchShift) {
  const key = resolveShiftPricingKey(searchShift);
  if (!key) return null;
  const pricing = getCaregiverShiftPricing(caregiver);
  const row = pricing?.[key];
  if (row && row.active !== false) {
    const fromShift = readPositiveInr(row.monthlyPriceInr ?? row.monthlyPrice);
    if (fromShift != null) return fromShift;
  }
  return null;
}

export function getExpectedMonthlySalaryInr(caregiver) {
  return readPositiveInr(
    caregiver?.salaryExpectation ?? caregiver?.salary_expectation ?? caregiver?.expectedSalary
  );
}

/** True when engagement should show monthly salary (duration > 30 days). */
export function shouldShowMonthlySalaryForDays(bookingDays) {
  const days = Math.max(1, Number(bookingDays) || 1);
  return days >= MONTHLY_SALARY_MIN_BOOKING_DAYS;
}

/**
 * Resolve agreement billing amounts for 12h / 24h engagements.
 * @returns {{ mode: "daily"|"monthly"|null, dailyRateInr: number|null, monthlySalaryInr: number|null }}
 */
export function resolveShiftSalaryBilling({ caregiver, searchShift, bookingDays = 1 } = {}) {
  const key = resolveShiftPricingKey(searchShift);
  if (!key) {
    return { mode: null, dailyRateInr: null, monthlySalaryInr: null };
  }
  const dailyRateInr = getShiftDailyRateInr(caregiver, searchShift);
  const monthlySalaryInr =
    getShiftMonthlyRateInr(caregiver, searchShift) ?? getExpectedMonthlySalaryInr(caregiver);
  if (shouldShowMonthlySalaryForDays(bookingDays)) {
    return {
      mode: "monthly",
      dailyRateInr,
      monthlySalaryInr,
    };
  }
  return {
    mode: "daily",
    dailyRateInr,
    monthlySalaryInr,
  };
}
